/**
 * Recently opened collections from the search palette, newest first.
 * Stored per browser in localStorage; every read/write is guarded because
 * storage can be unavailable (private mode, blocked storage, SSR).
 */
export type RecentSearch = {
	slug: string
	name: string
	/** Collection icon URL if the search result carried one. */
	icon?: string | null
	/** Enough of the collection object for CollectionIcon to render. */
	collection?: any
	at: number
}

const KEY = 'ow:recent-searches:v1'
const MAX = 8

export function getRecentSearches(): RecentSearch[] {
	if (typeof window === 'undefined') return []
	try {
		const raw = window.localStorage.getItem(KEY)
		const list = raw ? JSON.parse(raw) : []
		return Array.isArray(list) ? list.filter((e) => e && typeof e.slug === 'string') : []
	} catch {
		return []
	}
}

export function addRecentSearch(entry: Omit<RecentSearch, 'at'>) {
	if (typeof window === 'undefined' || !entry?.slug) return
	try {
		const next = [
			{ ...entry, at: Date.now() },
			...getRecentSearches().filter((e) => e.slug !== entry.slug)
		].slice(0, MAX)
		window.localStorage.setItem(KEY, JSON.stringify(next))
		window.dispatchEvent(new Event('ow:recent-searches'))
	} catch {
		/* storage unavailable */
	}
}

export function clearRecentSearches() {
	if (typeof window === 'undefined') return
	try {
		window.localStorage.removeItem(KEY)
		window.dispatchEvent(new Event('ow:recent-searches'))
	} catch {
		/* storage unavailable */
	}
}

/** Keep only the fields the palette needs so the entry stays small. */
export function toRecentCollection(c: any) {
	if (!c) return null
	return {
		slug: c.slug,
		name: c.name,
		icon: c.icon ?? null,
		icon_inscription_id: c.icon_inscription_id ?? c.icon_inscription?.id ?? null,
		icon_inscription: c.icon_inscription
			? { id: c.icon_inscription.id, content_type: c.icon_inscription.content_type ?? null }
			: c.icon_inscription_id
			? { id: c.icon_inscription_id, content_type: null }
			: null,
		verified: Boolean(c.verified),
		collection_object_type: c.collection_object_type ?? c.type ?? null,
		gallery_inscription_id: c.gallery_inscription_id ?? null
	}
}
